import { useCallback, useEffect, useState } from "react";

const DISMISS_KEY = "gourmeto-install-dismissed";

let sharedPrompt = null;
const promptListeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    sharedPrompt = event;
    promptListeners.forEach((fn) => fn());
  });
  window.addEventListener("appinstalled", () => {
    sharedPrompt = null;
    promptListeners.forEach((fn) => fn());
  });
}

const isStandaloneDisplay = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const isIosDevice = () => {
  if (typeof window === "undefined") return false;
  const { userAgent, maxTouchPoints } = window.navigator;
  if (/iphone|ipad|ipod/i.test(userAgent)) return true;
  // iPadOS 13+ Safari reports a desktop Macintosh user agent.
  return /macintosh/i.test(userAgent) && maxTouchPoints > 1;
};

// The install banner is only relevant on a phone or tablet. A Mac or PC must
// never be told to put an icon on its home screen.
const isHandheldDevice = () => {
  if (typeof window === "undefined") return false;
  if (window.navigator.userAgentData?.mobile) return true;
  if (isIosDevice()) return true;
  return /android|mobile|iemobile|opera mini/i.test(window.navigator.userAgent);
};

export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(sharedPrompt);
  const [isInstalled, setIsInstalled] = useState(isStandaloneDisplay);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const isIos = isIosDevice();
  const isHandheld = isHandheldDevice();

  useEffect(() => {
    if (isStandaloneDisplay()) {
      setIsInstalled(true);
      return undefined;
    }

    const sync = () => {
      setDeferredPrompt(sharedPrompt);
      if (!sharedPrompt && isStandaloneDisplay()) setIsInstalled(true);
    };

    promptListeners.add(sync);
    sync();
    return () => promptListeners.delete(sync);
  }, []);

  const promptInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      sharedPrompt = null;
      setDeferredPrompt(null);
      promptListeners.forEach((fn) => fn());
      return;
    }
    setShowIosHelp(true);
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setShowIosHelp(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
  }, []);

  // Either the browser gave us a real install prompt, or we are on iOS where
  // the only route is the manual Share sheet. Anything else has no way in.
  const canInstall = Boolean(deferredPrompt) || isIos;

  return {
    isInstalled,
    isIos,
    isHandheld,
    canInstall,
    canPrompt: Boolean(deferredPrompt),
    showIosHelp,
    showBanner: isHandheld && canInstall && !isInstalled && !dismissed,
    promptInstall,
    dismiss,
  };
}
